import { useCallback, useEffect, useRef, useState } from 'react'

import type { Conversation } from './conversations-model'
import type { InteractionOperationalState } from './interaction-operational-contracts'
import type { InteractionOperationalRepository } from './interaction-operational-repository'
import { hintedRefresh } from '../../services/hinted-refresh'

const QUERY_BATCH_SIZE = 100
// Review, star and Action Request changes, and aggregate activity (the only
// conversation input to this state), arrive as hints. The fallback only reconciles a missed hint; without live
// hints it is the old fixed cadence.
const HINTED_FALLBACK_MS = 120_000
const UNHINTED_INTERVAL_MS = 10_000

export function useInteractionOperationalState(
  repository: InteractionOperationalRepository | undefined,
  tenantId: string,
  conversations: Conversation[]
) {
  const generation = useRef(0)
  const [states, setStates] = useState<
    Record<string, InteractionOperationalState>
  >({})
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle'
  )
  // Counts completed refreshes, so views derived from these states can
  // refresh with them even when nothing in a state changed.
  const [refreshes, setRefreshes] = useState(0)
  // A failed read may recover without a hint of its own: keep the fast
  // cadence until a read succeeds.
  const lastReadFailed = useRef(false)
  const scheduled = useRef<(() => Promise<void>) | null>(null)
  const aggregateIds = conversations.flatMap((conversation) =>
    conversation.aggregateId ? [conversation.aggregateId] : []
  )
  const key = aggregateIds.join(':')

  const refresh = useCallback(async () => {
    if (!repository || aggregateIds.length === 0) {
      setStates({})
      setStatus('idle')
      return
    }
    const requestGeneration = generation.current
    try {
      const batches = Array.from(
        { length: Math.ceil(aggregateIds.length / QUERY_BATCH_SIZE) },
        (_, index) =>
          aggregateIds.slice(
            index * QUERY_BATCH_SIZE,
            (index + 1) * QUERY_BATCH_SIZE
          )
      )
      const items = (
        await Promise.all(
          batches.map((batch) => repository.query(tenantId, batch))
        )
      ).flat()
      if (generation.current !== requestGeneration) return
      setStates(
        Object.fromEntries(items.map((item) => [item.aggregateId, item]))
      )
      lastReadFailed.current = false
      setStatus('ready')
      setRefreshes((count) => count + 1)
    } catch {
      if (generation.current !== requestGeneration) return
      lastReadFailed.current = true
      setStatus('error')
    }
  }, [key, repository, tenantId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    generation.current += 1
    setStates({})
    setStatus(repository && aggregateIds.length ? 'loading' : 'idle')
    if (!repository || aggregateIds.length === 0) return
    const stop = hintedRefresh(
      tenantId,
      ['operational', 'aggregates'],
      refresh,
      {
        healthyMs: HINTED_FALLBACK_MS,
        unhealthyMs: UNHINTED_INTERVAL_MS,
        urgent: () => lastReadFailed.current,
      }
    )
    scheduled.current = stop.refresh
    return () => {
      stop()
      if (scheduled.current === stop.refresh) scheduled.current = null
      generation.current += 1
    }
  }, [key, refresh, repository, tenantId]) // eslint-disable-line react-hooks/exhaustive-deps

  const update = useCallback((state: InteractionOperationalState) => {
    setStates((current) => ({ ...current, [state.aggregateId]: state }))
    setStatus('ready')
  }, [])

  // Callers' refreshes (after a command) share the schedule, so a failure
  // there also restores the fast cadence.
  const refreshNow = useCallback(
    () => (scheduled.current ? scheduled.current() : refresh()),
    [refresh]
  )

  return { states, status, refreshes, refresh: refreshNow, update }
}
